import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check, Clock } from "lucide-react";

const CAL_LINK = "https://cal.com/kalza-studio-hdho6x";

export default function ServicesPage() {
  return (
    <main data-testid="services-page">
      {/* Hero — left-aligned, consistent font sizes */}
      <section className="pt-32 pb-20 md:pb-28 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/grey-waves.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1C1210]/85" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="font-eyebrow text-[#D4A0A0] mb-6 block">HOW I CAN HELP</span>
            <h1 className="mb-6">
              <span className="font-display text-4xl md:text-5xl lg:text-6xl text-white block leading-[0.95]">STRATEGIC CLARITY</span>
              <span className="font-accent text-4xl md:text-5xl lg:text-6xl text-[#BE123C] block mt-2 leading-[1.1]">Real transformation</span>
            </h1>
            <p className="font-body text-base md:text-lg text-white/60 leading-relaxed max-w-xl">
              This work is for founders whose business has grown, but whose brand is still behind. I help you find what is off, highlight what matters and build a brand that feels true and works in practice.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Start Here */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
            <h3 className="font-serif text-xl text-[#1C1210] mb-6 text-center">Start here</h3>
            <p className="font-body text-base text-[#6B6560] leading-relaxed text-center mb-8">
              Choose the offer that matches where you are right now.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { need: "You need clarity before making bigger brand decisions", offer: "Brand Reality Check Sprint", link: "/sprint" },
                { need: "You need help getting clear on your brand and message", offer: "Brand Foundation", link: null },
                { need: "You need both strategy and a full visual system", offer: "Brand Foundation & Visual Identity System", link: null },
                { need: "You need a campaign idea that makes people feel the offer fast", offer: "Campaign Concept", link: null },
              ].map((item, i) => (
                <div key={i} className="bg-[#F5F0EB] rounded-xl p-5">
                  <p className="font-body text-sm text-[#6B6560] mb-2">{item.need}</p>
                  <p className="font-serif text-base text-[#1C1210]">{"\u2192"} {item.link ? <Link to={item.link} className="text-[#BE123C] hover:underline">{item.offer}</Link> : item.offer}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Think of it like a person */}
      <section className="py-16 md:py-20 bg-[#F5F0EB]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <span className="font-eyebrow text-[#8B1A3A] mb-4 block">WHAT IS BRAND IDENTITY ACTUALLY</span>
            <h2 className="mb-10">
              <span className="font-display text-3xl md:text-4xl text-[#1C1210]">THINK OF IT LIKE A </span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C]">person</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16">
              <div>
                <h3 className="font-serif text-xl text-[#1C1210] mb-3">Brand identity is the personality</h3>
                <p className="font-body text-base text-[#6B6560] leading-relaxed">Who you are as a business. What you stand for, how you talk, what you believe in. When someone describes you to a friend, this is what they say.</p>
              </div>
              <div>
                <h3 className="font-serif text-xl text-[#1C1210] mb-3">Visual identity is the looks</h3>
                <p className="font-body text-base text-[#6B6560] leading-relaxed">Your logo, colours, fonts, the way your Instagram grid feels when someone lands on it for the first time. The outfit your brand puts on every morning. It's what people see before they hear a single word from you.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 space-y-10">

          {/* Sprint — flagship */}
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <div className="bg-[#1C1210] rounded-2xl overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-5">
                <div className="lg:col-span-2 h-56 lg:h-auto overflow-hidden">
                  <img src="/images/mood/sand-drawing.jpg" alt="" className="w-full h-full object-cover" />
                </div>
                <div className="lg:col-span-3 p-10 md:p-12 lg:p-14">
                  <div className="flex items-center gap-3 mb-5">
                    <span className="font-eyebrow text-[9px] tracking-[2px] text-white bg-[#BE123C] px-3 py-1 rounded-full">FLAGSHIP</span>
                    <span className="font-body text-xs text-white/50 flex items-center gap-1.5"><Clock size={12} /> 7 business days</span>
                  </div>
                  <div className="mb-6">
                    <h2 className="font-serif text-2xl md:text-3xl text-white leading-tight">Brand Reality Check Sprint</h2>
                    <p className="font-accent text-base text-[#D4A0A0] mt-1">See what is really going on</p>
                  </div>
                  <p className="font-body text-sm text-white/60 leading-[1.7] mb-8 max-w-lg">A focused sprint where I go through your brand presence, messaging and positioning to find what is off and what is making the business look weaker than it is. You get a clear plan for what to fix first.</p>
                  <p className="font-eyebrow text-[10px] text-[#D4A0A0] mb-4">YOU LEAVE WITH:</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5 mb-10">
                    {["Intake questionnaire and strategic conversation", "6-dimension brand diagnostic", "Competitor analysis (3-5 competitors)", "Communication gaps analysis", "Brand Reality Check Report", "30-day priority action plan"].map((item, j) => (
                      <div key={j} className="flex items-start gap-2.5">
                        <Check size={13} className="text-[#BE123C] flex-shrink-0 mt-0.5" />
                        <span className="font-body text-sm text-white/60 leading-relaxed">{item}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Link to="/sprint" className="inline-flex items-center justify-center gap-2 bg-[#E11D48] text-white font-body font-medium text-xs tracking-[2px] uppercase px-8 py-4 hover:bg-[#BE123C] transition-colors duration-300">
                      SEE FULL DETAILS <ArrowRight size={14} />
                    </Link>
                    <Link to="/sprint" className="inline-flex items-center justify-center gap-2 border border-white/15 text-white/50 font-body font-medium text-xs tracking-[2px] uppercase px-8 py-4 hover:text-white hover:border-white/30 transition-colors duration-300">
                      GET YOUR SPRINT
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Brand Foundation */}
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <div className="bg-[#F5F0EB] rounded-2xl overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-5">
                <div className="lg:col-span-2 h-56 lg:h-auto overflow-hidden">
                  <img src="/images/mood/grey-waves.jpg" alt="" className="w-full h-full object-cover" />
                </div>
                <div className="lg:col-span-3 p-10 md:p-12 lg:p-14">
                  <span className="font-body text-xs text-[#6B6560]/60 flex items-center gap-1.5 mb-5"><Clock size={12} /> 2-4 weeks</span>
                  <div className="mb-6">
                    <h2 className="font-serif text-2xl md:text-3xl text-[#1C1210] leading-tight">Brand Foundation</h2>
                    <p className="font-accent text-base text-[#BE123C] mt-1">Build the core of your brand</p>
                  </div>
                  <p className="font-body text-sm text-[#6B6560] leading-[1.7] mb-8 max-w-lg">A focused strategy workshop where we figure out what your brand stands for, who it is for and what makes it different. You leave with a brand book and a custom GPT trained on your voice.</p>
                  <p className="font-eyebrow text-[10px] text-[#8B1A3A] mb-4">YOU LEAVE WITH:</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5 mb-10">
                    {["Brand strategy workshop (2 sessions)", "Positioning and messaging framework", "Brand voice and tone guidelines", "Complete brand book", "Custom GPT trained on your brand voice", "Implementation guidance"].map((item, j) => (
                      <div key={j} className="flex items-start gap-2.5">
                        <Check size={13} className="text-[#BE123C] flex-shrink-0 mt-0.5" />
                        <span className="font-body text-sm text-[#4A4540] leading-relaxed">{item}</span>
                      </div>
                    ))}
                  </div>
                  <a href={CAL_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#BE123C] text-white font-body font-medium text-xs tracking-[2px] uppercase px-8 py-4 hover:bg-[#9F1239] transition-colors duration-300">
                    GET IN TOUCH <ArrowRight size={14} />
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Brand Foundation + Visual Identity */}
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <div className="bg-[#F5F0EB] rounded-2xl overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-5">
                <div className="lg:col-span-2 h-56 lg:h-auto overflow-hidden">
                  <img src="/images/mood/flower-hand.jpg" alt="" className="w-full h-full object-cover" />
                </div>
                <div className="lg:col-span-3 p-10 md:p-12 lg:p-14">
                  <span className="font-body text-xs text-[#6B6560]/60 flex items-center gap-1.5 mb-5"><Clock size={12} /> ~2 months</span>
                  <div className="mb-6">
                    <h2 className="font-serif text-2xl md:text-3xl text-[#1C1210] leading-tight">Brand Foundation &<br className="hidden md:block" /> Visual Identity System</h2>
                    <p className="font-accent text-base text-[#BE123C] mt-1">The full build</p>
                  </div>
                  <p className="font-body text-sm text-[#6B6560] leading-[1.7] mb-8 max-w-lg">Everything from Brand Foundation, plus a full visual identity designed around who you actually are. Logo, colors, typography, brand elements and practical templates you will use every day. Plus one month of my 1:1 support.</p>
                  <p className="font-eyebrow text-[10px] text-[#8B1A3A] mb-4">YOU LEAVE WITH:</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5 mb-10">
                    {["Everything in Brand Foundation", "Logo and visual identity system", "Typography and color system", "Brand templates for daily use", "Social media visual direction", "1 month of 1:1 rollout support"].map((item, j) => (
                      <div key={j} className="flex items-start gap-2.5">
                        <Check size={13} className="text-[#BE123C] flex-shrink-0 mt-0.5" />
                        <span className="font-body text-sm text-[#4A4540] leading-relaxed">{item}</span>
                      </div>
                    ))}
                  </div>
                  <a href={CAL_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#BE123C] text-white font-body font-medium text-xs tracking-[2px] uppercase px-8 py-4 hover:bg-[#9F1239] transition-colors duration-300">
                    GET IN TOUCH <ArrowRight size={14} />
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Campaign Concept */}
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <div className="bg-[#F5F0EB] rounded-2xl overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-5">
                <div className="lg:col-span-2 h-56 lg:h-auto overflow-hidden">
                  <img src="/images/mood/desert-sun.jpg" alt="" className="w-full h-full object-cover" />
                </div>
                <div className="lg:col-span-3 p-10 md:p-12 lg:p-14">
                  <span className="font-body text-xs text-[#6B6560]/60 flex items-center gap-1.5 mb-5"><Clock size={12} /> Scope-dependent</span>
                  <div className="mb-6">
                    <h2 className="font-serif text-2xl md:text-3xl text-[#1C1210] leading-tight">Campaign Concept</h2>
                    <p className="font-accent text-base text-[#BE123C] mt-1">Make your offer land</p>
                  </div>
                  <p className="font-body text-sm text-[#6B6560] leading-[1.7] mb-8 max-w-lg">I take your offer, dig into what makes it matter and build a campaign concept your audience can feel the moment they see it. The kind that makes buying feel easier because the message is clear and the visuals carry the right energy.</p>
                  <p className="font-eyebrow text-[10px] text-[#8B1A3A] mb-4">YOU LEAVE WITH:</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5 mb-10">
                    {["Offer analysis and audience insight", "Campaign concept and creative direction", "Two creative directions to choose from", "Full asset production plan", "Rollout strategy"].map((item, j) => (
                      <div key={j} className="flex items-start gap-2.5">
                        <Check size={13} className="text-[#BE123C] flex-shrink-0 mt-0.5" />
                        <span className="font-body text-sm text-[#4A4540] leading-relaxed">{item}</span>
                      </div>
                    ))}
                  </div>
                  <a href={CAL_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#BE123C] text-white font-body font-medium text-xs tracking-[2px] uppercase px-8 py-4 hover:bg-[#9F1239] transition-colors duration-300">
                    GET IN TOUCH <ArrowRight size={14} />
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

        </div>
      </section>

      {/* What This Changes */}
      <section className="py-20 md:py-28 bg-[#1C1210]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <span className="font-eyebrow text-[#D4A0A0] mb-4 block">WHAT THIS CHANGES</span>
            <h2 className="mb-12">
              <span className="font-display text-3xl md:text-4xl text-white block leading-[0.95]">AFTER WE WORK</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block mt-1 leading-[1.1]">together</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { title: "Clarity", body: "You stop struggling with what to say. The messaging comes naturally because you know what your brand is about and what it is not." },
                { title: "Confidence", body: "You feel more grounded in your decisions. Pricing gets easier because you understand your value and can stand behind it." },
                { title: "Consistency", body: "Your brand stops looking different every week. Everything works together so people recognise you and remember what you do." },
              ].map((item, i) => (
                <div key={i} className="border-l-2 border-[#BE123C]/40 pl-6">
                  <h3 className="font-serif text-xl text-white mb-3">{item.title}</h3>
                  <p className="font-body text-sm text-white/60 leading-relaxed">{item.body}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 bg-[#F5F0EB]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <span className="font-eyebrow text-[#8B1A3A] mb-4 block">FAQ</span>
            <h2 className="mb-10">
              <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">A FEW THINGS</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block mt-1 leading-[1.1]">you may be wondering</span>
            </h2>
            <div className="space-y-10">
              {[
                { q: "What if I am not sure which offer I need?", a: "That is completely normal. Most founders feel that way at the start. If you are not sure where to begin, the Brand Reality Check Sprint is usually the right first step. It gives you clarity on what is actually going on with your brand before you commit to anything bigger." },
                { q: "Do I need visuals or just strategy?", a: "If you are not sure what your brand should say or who it is really for, we start with strategy. If you already know that but the way it looks and feels does not match, then it is time to build the visual side. And if you are still not sure, that is what the first call is for." },
                { q: "Can we work together if my business is still changing?", a: "Yes. Many of my clients come to me while things are shifting. That is often the best time to do this work. The process helps you see what is already true about where you are headed, so you stop guessing and start building from a clearer place." },
                { q: "How long before I see results?", a: "That depends on the offer. The Sprint takes 7 business days and you walk away with a clear action plan. Brand Foundation takes 2 to 4 weeks. The full identity build is around 2 months. But most clients say the biggest shift happens early, when they finally see their business through someone else's eyes." },
                { q: "Is this only for businesses that are already established?", a: "No. I work with founders at different stages. What matters is that you have something real, a skill, an offer, a business that works, and the brand is just not reflecting it yet. If that is where you are, we can work together." },
              ].map((faq, i) => (
                <div key={i}>
                  <h3 className="font-serif text-lg text-[#1C1210] mb-3">{faq.q}</h3>
                  <p className="font-body text-base text-[#6B6560] leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Not Sure CTA */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <span className="font-accent text-base text-[#BE123C] block mb-4">Not sure which is right?</span>
            <h2 className="font-serif text-2xl md:text-3xl text-[#1C1210] mb-4">
              Book a brand clarity call and we will figure it out together
            </h2>
            <p className="font-body text-base text-[#6B6560] leading-relaxed mb-8 max-w-lg mx-auto">
              30 minutes, no selling. I will ask about your business, take a quick look at what is going on and tell you what I would focus on first.
            </p>
            <a href={CAL_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#E11D48] text-white font-body font-medium text-xs tracking-[2px] uppercase px-10 py-5 hover:bg-[#BE123C] transition-colors duration-300">
              BOOK A BRAND CLARITY CALL <ArrowUpRight size={16} />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/arch-sea.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1C1210]/80" />
        </div>
        <div className="relative max-w-3xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <div className="bg-[#1C1210]/90 backdrop-blur-sm px-10 py-14 md:px-16 md:py-20 rounded-2xl text-center">
              <h2 className="mb-4">
                <span className="font-serif text-3xl md:text-4xl text-white block">Your brand should match the level</span>
                <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block mt-2">you are at now</span>
              </h2>
              <p className="font-body text-base md:text-lg text-white/60 mb-10 max-w-xl mx-auto">
                If your business is stronger than the way it currently looks, sounds, or sells, that is usually the sign that something needs to be clarified first. We can start there.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href={CAL_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#E11D48] text-white font-body font-medium text-xs tracking-[2px] uppercase px-10 py-5 hover:bg-[#BE123C] transition-colors duration-300">
                  BOOK A BRAND CLARITY CALL <ArrowUpRight size={16} />
                </a>
                <Link to="/sprint" className="inline-flex items-center justify-center gap-2 border border-white/20 text-white/60 font-body font-medium text-xs tracking-[2px] uppercase px-10 py-5 hover:text-white hover:border-white/40 transition-colors duration-300">
                  START WITH THE SPRINT <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
